import { ChangeEvent, StateSetter, useCallback } from 'react';
import { useLang } from 'hooks/lang';

import { Column, Row, Button, IconButton, LabeledInput, LabeledSelect } from 'components/Blocks';
import { PlusIcon, XIcon } from 'components/Icons';

import { FormTeamService } from './types';
import { getID } from 'utils/id';

interface CostVariablesProps {
  children?: never;
  service: FormTeamService | null;
  setService: StateSetter<FormTeamService | null>;
}

function CostVariables({ service, setService }: CostVariablesProps): JSX.Element {
  const { t } = useLang();

  const addCostVariable = useCallback(() => {
    setService((oldData) => {
      if (!oldData) return oldData;

      const { costVariables, ...old } = oldData;

      return {
        ...old,
        costVariables: [...costVariables, { name: '', type: 'NUMBER', id: getID() }],
      };
    });
  }, [setService]);

  const updateCostVariable = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      const { value, name } = target;
      const [property, index] = name.split('-');

      setService((old) => {
        const { costVariables, ...rest } = old as FormTeamService;

        return {
          ...rest,
          costVariables: costVariables.map((variable, variableIndex) =>
            Number(index) === variableIndex ? { ...variable, [property]: value } : variable,
          ),
        };
      });
    },
    [setService],
  );

  const removeVariable = useCallback(
    (index: number) => {
      setService((oldData) => {
        if (!oldData) return oldData;

        const { costVariables, costCalculation, ...old } = oldData;

        const toRemove = costVariables[index];

        const activeReferences = costCalculation.filter(({ base, value }) =>
          [base.ref, value?.ref].some((id) => id == toRemove.id),
        );

        return {
          ...old,

          costCalculation: costCalculation.filter(
            ({ id, base, value }) =>
              !activeReferences.find((ref) =>
                [base, value, id].some((removeId) => removeId === ref.id),
              ),
          ),

          costVariables: costVariables.filter((_, varIndex) => varIndex !== index),
        };
      });
    },
    [setService],
  );

  return (
    <Column gap="6">
      <Row>
        <p>{t('variables')}</p>

        <Button format="square" round onClick={addCostVariable}>
          <PlusIcon size={16} />
        </Button>
      </Row>

      <Column>
        {service?.costVariables.map(({ name, type }, index) => (
          <Row key={`costVariable-${index}`} gap="2">
            <span className="mb-0.5 inline-block self-end">{index + 1}.</span>

            <LabeledInput
              required
              size="small"
              name={`name-${index}`}
              label={t('name')}
              value={name}
              onChange={updateCostVariable}
            />

            <LabeledSelect
              name={`type-${index}`}
              value={type}
              onChange={updateCostVariable as () => void}
              label={t('type') as string}
              size="small"
              isSearchable={false}
              className="min-w-[100px]"
              options={[
                {
                  value: 'CURRENCY',
                  label: t('currency'),
                },
                {
                  value: 'NUMBER',
                  label: t('number'),
                },
              ]}
            />

            {!service?.costVariables.find((props) =>
              Object.values(props).every((value) => value === name || value === type),
            ) && (
              <IconButton
                className="mb-0.5 self-end"
                onClick={() => removeVariable(index)}
                size="small"
              >
                <XIcon />
              </IconButton>
            )}
          </Row>
        ))}
      </Column>
    </Column>
  );
}

export default CostVariables;
